import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import './GetTheApp.css';

/**
 * The web is a reading room: you can read what an author has made public, and
 * that is all. Anything that would write — liking, saving, commenting,
 * subscribing, following, publishing — opens this instead and points at the
 * app.
 *
 * Mirrors jpegs-me's component of the same name. Components call `useGetApp()`
 * and invoke it from the handler they would otherwise have wired to Firestore,
 * so the affordance still looks live and the reason for the redirect is
 * obvious the moment it is tapped.
 *
 * This is not only a UI convention: longreads.me never creates a Firebase auth
 * session at all (see firebase/config.js), so there is no identity here that
 * COULD like or follow. The dialog explains a constraint that already exists.
 */

export const APP_STORE_URL = 'https://apps.apple.com/app/id6803270561';
export const PLAY_STORE_URL =
  'https://play.google.com/store/apps/details?id=com.longreads.android';

const GetAppContext = createContext(() => {});

export const useGetApp = () => useContext(GetAppContext);

const detectPlatform = () => {
  const ua = typeof navigator === 'undefined' ? '' : navigator.userAgent || '';
  if (/iPhone|iPad|iPod/i.test(ua)) return 'ios';
  if (/Android/i.test(ua)) return 'android';
  return 'other';
};

export function GetTheAppProvider({ children }) {
  const [reason, setReason] = useState(null);

  // `open('save this entry')` reads as "Get the app to save this entry".
  const open = useCallback((what) => setReason(what || null), []);
  const close = useCallback(() => setReason(null), []);
  const value = useMemo(() => open, [open]);

  const platform = detectPlatform();

  return (
    <GetAppContext.Provider value={value}>
      {children}
      {reason !== null && (
        <div className="getapp-backdrop" onClick={close} role="presentation">
          <div
            className="getapp-card"
            role="dialog"
            aria-modal="true"
            aria-label="Get the Long reads app"
            onClick={(e) => e.stopPropagation()}
          >
            <button className="getapp-close" onClick={close} aria-label="Close">
              ×
            </button>

            <div className="getapp-mark" aria-hidden="true">
              lr<span>.</span>
            </div>

            <h2 className="getapp-title">
              {reason ? `Get the app to ${reason}` : 'Get the app'}
            </h2>
            <p className="getapp-body">
              longreads.me is for reading. Saving, liking, commenting, subscribing
              and writing all live in the app — and that is where you make an account.
            </p>

            <div className="getapp-actions">
              <a
                className={`getapp-btn ${platform === 'android' ? 'is-secondary' : ''}`}
                href={APP_STORE_URL}
                target="_blank"
                rel="noreferrer"
              >
                Download on the App Store
              </a>
              <a
                className={`getapp-btn ${platform === 'ios' ? 'is-secondary' : ''}`}
                href={PLAY_STORE_URL}
                target="_blank"
                rel="noreferrer"
              >
                Get it on Google Play
              </a>
            </div>

            <button className="getapp-dismiss" onClick={close}>
              Keep reading
            </button>
          </div>
        </div>
      )}
    </GetAppContext.Provider>
  );
}

export default GetTheAppProvider;
